import React, { useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import LoadingModal from "../../../../../../components/LoadingModal";
import { Bookmark } from "../../../../../../models/bookmark";
import { Task } from "../../../../../../models/task";
import BookmarkService from "../../../../../../services/bookmark.service";
import { VideoStatus } from "../../../../../../util/enums";
import BookmarkTimeline from "./Bookmark";
import CommentsTimeline from "./Comments";
import ScreenshotsList from "./ScreenshotSurvey";
import useStyles from "./styles";
import VideoTask from "./Video";

interface Props {
  taskId?: number;
  tasks?: Task[];
  isOnViewMode?: boolean;
  isUpdating?: boolean;
  isSavingAndSending?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onSaveAndSend?: () => void;
}

const VideoSurvey = ({
  taskId,
  tasks,
  isUpdating,
  isOnViewMode = false,
  isSavingAndSending = false,
  onSave,
  onCancel,
  onSaveAndSend,
}: Props) => {
  const classes = useStyles(isOnViewMode)();

  const renderTaskContent = () => {
    if (!tasks || tasks.length <= 1) {
      return <VideoSurveyContent taskId={taskId} hasBeenDeleted={tasks?.[0]?.statusVideo} />;
    }

    if (tasks && tasks.length > 0) {
      const hasVideoDeleted = tasks.find((task) => task.statusVideo === VideoStatus.DELETED);

      if (hasVideoDeleted) {
        return <VideoSurveyContent taskId={taskId} hasBeenDeleted={VideoStatus.DELETED} />;
      }
    }

    return tasks.map((task, index) => (
      <div key={task.id} className={index !== tasks.length - 1 ? classes.separator : classes.mb32}>
        <p className={classes.date}>{format(new Date(task.startDate), "dd/MM/yyyy hh:mm aa")}</p>
        <VideoSurveyContent taskId={task?.id} />
      </div>
    ));
  };

  return (
    <div className={classes.videoSurveyWrapper}>
      {renderTaskContent()}
      {!isOnViewMode && (
        <div className={classes.buttons}>
          <ActionButtons
            showSaveAndSendButton={isSavingAndSending}
            cancelDisabled={isUpdating}
            saveDisabled={isUpdating}
            onCancel={onCancel}
            onSave={onSave}
            onSaveAndSend={onSaveAndSend}
          />
        </div>
      )}
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

interface VideoSurveyContentProps {
  taskId?: number;
  hasBeenDeleted?: string;
}

const VideoSurveyContent = ({ taskId, hasBeenDeleted }: VideoSurveyContentProps) => {
  const [comments, setComments] = useState<Bookmark[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);

  const { isFetching } = useQuery(
    [`bookmark.list.${taskId}`, taskId],
    () =>
      BookmarkService.getAll({
        taskId,
        include: JSON.stringify([{ association: "user" }]),
      }),
    {
      enabled: !!taskId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const bookmarksFiltered = response.data.filter((data) => !data.comments);
          const commentsFiltered = response.data.filter((data) => !!data.comments);

          setComments(commentsFiltered);
          setBookmarks(bookmarksFiltered);
        }
      },
    }
  );

  return (
    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={3}>
      <Grid item container direction="column" xs={12} sm={12} md={6} lg={6}>
        <VideoTask taskId={taskId} videoHasBeenDeleted={hasBeenDeleted} />
        <BookmarkTimeline isFetching={isFetching} bookmarks={bookmarks} />
      </Grid>
      <Grid item container direction="column" xs={12} sm={12} md={6} lg={6}>
        <ScreenshotsList taskId={taskId} />
        <CommentsTimeline isFetching={isFetching} comments={comments} />
      </Grid>
    </Grid>
  );
};

export default VideoSurvey;
